import { RefObject, useEffect, useState } from "react";

import { VirtualScroller } from "primereact/virtualscroller";

import { THREAD_PAGE_SIZE } from "../../../../stores";

interface IUseThreadHistoryVirtualItemSizeArgs {
  virtualScrollerRef: RefObject<VirtualScroller | null>;
  showDateSeparators?: boolean;
  isSidebarCollapsed: boolean;
  isOverlaySidebarVisible: boolean;
  initialItemSize?: number;
}

// #66976 measures a stable per-row size and avoids recalculating it on every page append,
// which would otherwise shift the virtual spacer and cause visible jumps
export const useThreadHistoryVirtualItemSize = ({
  virtualScrollerRef,
  showDateSeparators,
  isSidebarCollapsed,
  isOverlaySidebarVisible,
  initialItemSize = 50,
}: IUseThreadHistoryVirtualItemSizeArgs): number => {
  const [virtualItemSize, setVirtualItemSize] = useState(initialItemSize);

  useEffect(() => {
    const getScrollerElement = () => {
      const elementRef = virtualScrollerRef.current?.getElementRef() as { current?: HTMLDivElement | null } | undefined;
      return elementRef?.current ?? null;
    };

    const recalculateItemSize = () => {
      const scrollerElement = getScrollerElement();
      if (!scrollerElement) {
        return;
      }

      const sampledRows = Array.from(scrollerElement.querySelectorAll<HTMLElement>(".thread-list-row")).slice(
        0,
        THREAD_PAGE_SIZE,
      );
      if (!sampledRows.length) {
        return;
      }

      const nonSeparatorRows = sampledRows.filter(row => !row.querySelector(".thread-list-row-separator"));
      const rowsToMeasure = nonSeparatorRows.length ? nonSeparatorRows : sampledRows;

      const sampledHeights = rowsToMeasure
        .map(row => Math.ceil(row.getBoundingClientRect().height))
        .filter(height => height > 0);

      if (!sampledHeights.length) {
        return;
      }

      // uses the tallest sampled row to set the virtualItemSize
      const measuredItemSize = Math.max(...sampledHeights);

      // during mount/unmount there can be transient invalid reads
      // keeps the last known valid size
      if (!Number.isFinite(measuredItemSize) || measuredItemSize <= 0) {
        return;
      }

      setVirtualItemSize(prev => (prev === measuredItemSize ? prev : measuredItemSize));
    };

    // waits for paint so layout-dependent measurements are accurate
    const animationFrameId = window.requestAnimationFrame(recalculateItemSize);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [showDateSeparators, isSidebarCollapsed, isOverlaySidebarVisible, initialItemSize, virtualScrollerRef]);

  return virtualItemSize;
};
